import difference from 'lodash/difference';
import findIndex from 'lodash/findIndex';
import includes from 'lodash/includes';
import map from 'lodash/map';
import toNumber from 'lodash/toNumber';
import uniq from 'lodash/uniq';

import { tableSettingsByList } from 'utils/helpers';
import { immutableSet } from 'utils/immutableSet';
import { STORAGE_KEYS } from 'utils/constants/constants';
import { EEditType } from 'types/commonEnums';
import { EListsActions, EListsTypes, IListsReducer, IListsTags, TListsActions } from 'types/listsTypes';
import { ECommonActions } from 'store/reducers/types';

const productsLimit = toNumber(localStorage.getItem(STORAGE_KEYS.productsLimit) || '10');
const ordersLimit = toNumber(localStorage.getItem(STORAGE_KEYS.ordersLimit) || '10');

const initialState: IListsReducer = {
  [EListsTypes.ProductsType]: {
    results: [],
    pagination: {
      isNeedUpdate: true,
      isLoading: true,
      limit: productsLimit,
      page: 1,
      offset: 0,
      totalResults: 0,
      condition: 'any',
      orderBy: '',
      isSelectedAll: false,
      syncStatus: null,
    },
    activeFilters: [],
    savedFilters: {
      isUpdated: false,
      filters: [],
    },
    tags: {
      isUpdated: false,
      tagsList: [],
    },
    shopifyCollections: {
      isUpdated: false,
      list: [],
    },
    isBulkActivating: false,
    tableSettings: tableSettingsByList(EListsTypes.ProductsType),
  },
  [EListsTypes.OrdersType]: {
    results: [],
    pagination: {
      isNeedUpdate: true,
      isLoading: true,
      limit: ordersLimit,
      page: 1,
      offset: 0,
      totalResults: 0,
      condition: 'any',
      orderBy: '',
      isSelectedAll: false,
      syncStatus: null,
    },
    activeFilters: [],
    savedFilters: {
      isUpdated: false,
      filters: [],
    },
    tags: {
      isUpdated: false,
      tagsList: [],
    },
    tableSettings: tableSettingsByList(EListsTypes.OrdersType),
  },
};

const updateTagsState = (stateTags: IListsTags, tags: string[]) => ({
  ...stateTags,
  tagsList: tags
    ? uniq([...stateTags.tagsList, ...tags])
    : stateTags.tagsList,
});

export default function(state = initialState, action: TListsActions): IListsReducer {
  switch (action.type) {
    case EListsActions.GetListResultsByType: {
      const { listType, results, pagination } = action.payload;

      return {
        ...state,
        [listType]: {
          ...state[listType], results, pagination: { ...state[listType].pagination, ...pagination },
        },
      };
    }

    case EListsActions.SetPaginationByType: {
      const { listType, pagination } = action.payload;

      return {
        ...state,
        [listType]: {
          ...state[listType], pagination: { ...state[listType].pagination, ...pagination },
        },
      };
    }

    case EListsActions.SetActiveFiltersByType: {
      const { listType, activeFilters } = action.payload;

      return {
        ...state,
        [listType]: {
          ...state[listType],
          activeFilters,
          pagination: initialState[listType].pagination,
        },
      };
    }

    case EListsActions.UpdateTableSettingsByType: {
      const { listType, tableSettings } = action.payload;

      return { ...state, [listType]: { ...state[listType], tableSettings } };
    }

    case EListsActions.GetProductsTags: {
      return { ...state, products: { ...state.products, tags: action.payload } };
    }

    case EListsActions.GetProductsShopifyCollections: {
      return { ...state, products: { ...state.products, shopifyCollections: action.payload } };
    }

    case EListsActions.UpdateProduct: {
      const { id, updates } = action.payload;
      const productIndex = findIndex(state.products.results, { id });

      return {
        ...state,
        products: {
          ...state.products,
          results: productIndex >= 0
            ? immutableSet(
              state.products.results,
              productIndex,
              { ...state.products.results[productIndex], ...updates },
            )
            : state.products.results,
          tags: updateTagsState(state.products.tags, updates.tags),
        },
      };
    }

    case EListsActions.BulkUpdateProducts: {
      const { ids, type, updates } = action.payload;

      return {
        ...state,
        products: {
          ...state.products,
          results: map(state.products.results, (product) => includes(ids, product.id)
            ? {
              ...product,
              ...updates,
              tags: type === EEditType.Remove
                ? difference(product.tags, updates.tags)
                : uniq([...product.tags, ...(updates?.tags || [])]),
            }
            : product,
          ),
          tags: updateTagsState(state.products.tags, updates.tags),
        },
      };
    }

    case EListsActions.SaveFiltersByType: {
      const { listType, savedFilters } = action.payload;

      return {
        ...state,
        [listType]: {
          ...state[listType],
          savedFilters: {
            ...state[listType].savedFilters,
            filters: [...state[listType].savedFilters.filters, ...savedFilters],
          },
        },
      };
    }

    case EListsActions.IsBulkActivating: {
      return { ...state, products: { ...state.products, ...action.payload } };
    }

    case ECommonActions.CleanStore: {
      return initialState;
    }

    default:
      return state;
  }
}
